import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, Play, MousePointer2 } from "lucide-react";
import { MEDIA, BRAND } from "@/data/content";
import { openLeadModal } from "@/components/LeadModal";
import { scrollToSection } from "@/components/Navbar";

const LINES = ["We Turn Streets", "Into Your Brand's", "Biggest Stage."];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const videoY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const videoScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 20 });
  const sy = useSpring(my, { stiffness: 60, damping: 20 });

  const onMouseMove = (e) => {
    const { innerWidth, innerHeight } = window;
    mx.set((e.clientX / innerWidth - 0.5) * 24);
    my.set((e.clientY / innerHeight - 0.5) * 24);
  };

  return (
    <section ref={ref} id="top" data-testid="hero-section" onMouseMove={onMouseMove} className="relative h-[100svh] min-h-[640px] overflow-hidden">
      <motion.div style={{ y: videoY, scale: videoScale }} className="absolute inset-0">
        <video
          className="w-full h-full object-cover"
          src={MEDIA.heroVideo}
          poster={MEDIA.heroPoster}
          autoPlay
          muted
          loop
          playsInline
          data-testid="hero-bg-video"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-black" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(0,0,0,0.55)_100%)]" />
      <div className="glow-blob w-[34rem] h-[34rem] bg-indigo-600/20 -top-40 -left-40" />
      <div className="glow-blob w-[28rem] h-[28rem] bg-purple-600/15 bottom-0 right-0" />

      <motion.div style={{ opacity: contentOpacity, y: contentY }} className="relative z-10 h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-center">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="text-xs font-mono uppercase tracking-[0.35em] text-indigo-300 mb-6 flex items-center gap-3"
        >
          <span className="w-8 h-px bg-gradient-to-r from-blue-500 to-purple-500" />
          Brand Activation &amp; Experiential Marketing — India &amp; Beyond
        </motion.p>

        <h1 className="font-display font-extrabold uppercase tracking-tight leading-[1.02] text-4xl sm:text-6xl lg:text-7xl max-w-5xl" data-testid="hero-headline">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 2 ? "text-gradient" : "text-white"}`}
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ delay: 0.45 + i * 0.16, duration: 1, ease: [0.22, 1, 0.36, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-6 text-base sm:text-lg text-zinc-300 tracking-wide"
          data-testid="hero-subheading"
        >
          {BRAND.subheading}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <button
            data-testid="hero-strategy-call-btn"
            onClick={() => openLeadModal()}
            className="btn-gradient text-white font-semibold px-8 py-4 rounded-full inline-flex items-center justify-center gap-2 text-sm sm:text-base"
          >
            Book a Free Strategy Call <ArrowRight className="w-4 h-4" />
          </button>
          <button
            data-testid="hero-view-campaigns-btn"
            onClick={() => scrollToSection("#portfolio")}
            className="glass text-white font-semibold px-8 py-4 rounded-full inline-flex items-center justify-center gap-2 text-sm sm:text-base hover:border-indigo-400/50 transition-colors duration-300"
          >
            <Play className="w-4 h-4" /> View Our Campaigns
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        style={{ x: sx, y: sy }}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.6, duration: 0.9 }}
        className="hidden md:flex absolute bottom-36 right-10 lg:right-20 z-10 glass rounded-2xl px-6 py-5 flex-col gap-1 glow-border"
        data-testid="hero-floating-stats-card"
      >
        <span className="font-display text-2xl font-bold text-gradient">50M+</span>
        <span className="text-xs text-zinc-400 uppercase tracking-[0.2em]">Organic Views Generated</span>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-zinc-500"
      >
        <MousePointer2 className="w-4 h-4 animate-bounce" />
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
      </motion.div>
    </section>
  );
};

export default Hero;
